// src/components/SessionGuard.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "antd";
import { useAuth } from "../contexts/loginContext";

// Configuración de tiempos
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;      // 10 minutos
const MAX_SESSION_MS = 12 * 60 * 60 * 1000;  // 12 horas
const CHECK_INTERVAL_MS = 60 * 1000;         // revisar cada 1 minuto

// Claves en localStorage (separadas de tu STORAGE_KEY actual)
const LS_LOGIN_TIME = "session_loginTime";
const LS_LOGIN_DAY = "session_loginDay";
const LS_LAST_ACTIVITY = "session_lastActivity";

// Eventos que vamos a considerar como "actividad"
const ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "mousemove",
  "scroll",
  "touchstart",
];
const UPDATE_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const LS_APP_VERSION_SIGNATURE = "app_version_signature";

async function readAppVersionSignature() {
  const resp = await fetch(`/version.json?ts=${Date.now()}`, {
    method: "GET",
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!resp.ok) {
    throw new Error(`version_http_${resp.status}`);
  }
  const version = await resp.json();
  const appVersion = String(version?.appVersion || "").trim();
  const buildTime = String(version?.buildTime || "").trim();
  return [appVersion, buildTime].filter(Boolean).join("|");
}

async function hardReloadApplication() {
  try {
    localStorage.removeItem(LS_APP_VERSION_SIGNATURE);
  } catch (e) {
    console.warn("[SessionGuard] No se pudo resetear firma de versión:", e);
  }

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (e) {
    console.warn("[SessionGuard] No se pudo limpiar service workers:", e);
  }

  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } catch (e) {
    console.warn("[SessionGuard] No se pudo limpiar cache storage:", e);
  }

  const url = new URL(window.location.href);
  url.searchParams.set("__hard_refresh", String(Date.now()));
  window.location.assign(url.toString());
}

export default function SessionGuard({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    // Si NO hay usuario autenticado: limpia llaves de sesión y no hace nada más
    if (!user) {
      try {
        localStorage.removeItem(LS_LOGIN_TIME);
        localStorage.removeItem(LS_LOGIN_DAY);
        localStorage.removeItem(LS_LAST_ACTIVITY);
      } catch (e) {
        console.warn("[SessionGuard] Error limpiando localStorage sin usuario:", e);
      }
      return; // sin usuario no arrancamos timers
    }

    // Si hay usuario autenticado:
    const now = Date.now();
    const today = new Date().toISOString().slice(0, 10);

    try {
      if (!localStorage.getItem(LS_LOGIN_TIME)) {
        localStorage.setItem(LS_LOGIN_TIME, String(now));
      }
      if (!localStorage.getItem(LS_LOGIN_DAY)) {
        localStorage.setItem(LS_LOGIN_DAY, today);
      }
      if (!localStorage.getItem(LS_LAST_ACTIVITY)) {
        localStorage.setItem(LS_LAST_ACTIVITY, String(now));
      }
    } catch (e) {
      console.warn("[SessionGuard] No se pudo escribir en localStorage:", e);
    }

    const updateActivity = () => {
      try {
        localStorage.setItem(LS_LAST_ACTIVITY, String(Date.now()));
      } catch (e) {
        console.warn("[SessionGuard] No se pudo actualizar lastActivity:", e);
      }
    };

    // Escuchamos actividad del usuario
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, updateActivity)
    );

    // Intervalo para revisar expiración
    const intervalId = setInterval(async () => {
      try {
        const current = Date.now();
        const loginTime =
          parseInt(localStorage.getItem(LS_LOGIN_TIME) || `${current}`, 10) ||
          current;
        const lastActivity =
          parseInt(localStorage.getItem(LS_LAST_ACTIVITY) || `${current}`, 10) ||
          current;
        const loginDay = localStorage.getItem(LS_LOGIN_DAY) || today;
        const todayNow = new Date().toISOString().slice(0, 10);

        const idleElapsed = current - lastActivity;  // tiempo sin actividad
        const sessionElapsed = current - loginTime;  // tiempo total de sesión

        const idleExpired = idleElapsed > IDLE_TIMEOUT_MS;
        const maxSessionHit = sessionElapsed > MAX_SESSION_MS;
        const dayChanged = loginDay !== todayNow;

        if (idleExpired || maxSessionHit || dayChanged) {
          console.log("[SessionGuard] Sesión expirada por:", {
            idleExpired,
            maxSessionHit,
            dayChanged,
          });

          // Dejamos de escuchar actividad y paramos el interval
          ACTIVITY_EVENTS.forEach((ev) =>
            window.removeEventListener(ev, updateActivity)
          );
          clearInterval(intervalId);

          // Limpiamos las llaves de sesión
          try {
            localStorage.removeItem(LS_LOGIN_TIME);
            localStorage.removeItem(LS_LOGIN_DAY);
            localStorage.removeItem(LS_LAST_ACTIVITY);
          } catch (e) {
            console.warn("[SessionGuard] Error limpiando localStorage:", e);
          }

          try {
            // Cerramos sesión usando tu contexto (esto ya borra STORAGE_KEY y Firebase si aplica)
            await logout();
          } catch (e) {
            console.error("[SessionGuard] Error al llamar logout():", e);
          }

          // Enviamos al login con un motivo
          navigate("/login?reason=sesion_expirada", { replace: true });
        }
      } catch (e) {
        console.error("[SessionGuard] Error en chequeo de sesión:", e);
      }
    }, CHECK_INTERVAL_MS);

    // Cleanup cuando el componente se desmonte o cambie el usuario
    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, updateActivity)
      );
      clearInterval(intervalId);
    };
  }, [user, logout, navigate]);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const checkVersion = async () => {
      try {
        const currentSignature = await readAppVersionSignature();
        if (!currentSignature) return;
        const saved = localStorage.getItem(LS_APP_VERSION_SIGNATURE);
        if (!saved) {
          localStorage.setItem(LS_APP_VERSION_SIGNATURE, currentSignature);
          return;
        }
        if (saved !== currentSignature) {
          if (!cancelled) setUpdateReady(true);
        }
      } catch (e) {
        console.warn("[SessionGuard] Error verificando actualización:", e);
      }
    };

    checkVersion();
    const timer = setInterval(checkVersion, UPDATE_CHECK_INTERVAL_MS);

    const onFocus = () => {
      checkVersion();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [user]);

  return (
    <>
      {children}
      <Modal
        open={updateReady}
        title={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText="Actualizar ahora"
        okButtonProps={{ className: "update-modal__ok" }}
        wrapClassName="update-modal"
        cancelButtonProps={{ style: { display: "none" } }}
        onOk={hardReloadApplication}
      >
        <div className="update-modal__content">
          <img
            src="/logo192.png"
            alt="Logo del Portal del Trabajador"
            className="update-modal__logo"
          />
          <h3 className="update-modal__title">Hay una nueva actualizacion disponible</h3>
          <p className="update-modal__text">
            Hay una nueva versión del Portal del Trabajador lista para usar.
          </p>
          <p className="update-modal__text update-modal__text--muted">
            Haz clic en <strong>Actualizar ahora</strong> para recargar con los ultimos cambios.
          </p>
        </div>
      </Modal>
    </>
  );
}
